import { v5 as uuidv5 } from 'uuid';
import type { CanvasEdge, CanvasNode, NodeRelationType } from '@/types/visual-editor';

const TEMPLATE_SPLIT_OUTPUT_NODE_NAMESPACE = 'c8d9f1f4-b6de-4f1b-976d-0b2d4b0d3b0a';
const TASK_COMPLETION_NODE_NAMESPACE = 'f23e0e31-c7df-4f5a-86c0-c8d6f3efbe74';
const TASK_CANDIDATE_NODE_NAMESPACE = '4d2d81c0-222a-49a4-a548-c11f38750d88';
const TEMPLATE_RUN_OUTPUT_NODE_NAMESPACE = '4a55df9e-cf65-4720-a417-f0b55f349363';
const TEMPLATE_PROMPT_SEED_NODE_NAMESPACE = '1efb1328-f5c3-4171-8d99-4e19d6bc4f0f';
export { buildBrandStoryboardBoardNodeId } from '../../../../shared/conversation/brand-storyboard';
const UGC_STORYBOARD_BOARD_NODE_NAMESPACE = 'c2a967c7-8a5a-4f3a-a08b-083412a82d61';
const UGC_SEQUENCE_NODE_NAMESPACE = 'a0dbeb57-5d53-4d28-a0aa-462843f1cbd8';
const UGC_FINAL_VIDEO_NODE_NAMESPACE = 'de9af5c4-f85d-4a4b-8f78-14f3b5533f1e';
const VIDEO_STORYBOARD_NAMESPACE = 'bfb96efb-2b51-40c4-bbd7-93a3112a1ec4';
const VIDEO_STORYBOARD_CANVAS_NODE_NAMESPACE = '55eb7b50-5fb4-4cc9-93d5-a773c6961288';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type MaterializationKeyNode = Pick<
  CanvasNode,
  | 'decompositionParentId'
  | 'generatedByTaskId'
  | 'generatingCapability'
  | 'generatingTaskId'
  | 'generationCapability'
  | 'generationGroupId'
  | 'id'
  | 'mediaType'
  | 'modelUrl'
  | 'originalImageUrl'
  | 'outputIndex'
  | 'promptMetadata'
  | 'sequenceData'
  | 'storyboardData'
  | 'thumbnail'
  | 'thumbnailUrl'
  | 'videoUrl'
  | '_elementIndex'
>;

export function buildTemplateSplitOutputNodeId(taskId: string, outputIndex: number, splitOrdinal: number): string {
  return uuidv5(`${taskId}::${outputIndex}::${splitOrdinal}`, TEMPLATE_SPLIT_OUTPUT_NODE_NAMESPACE);
}

export function buildCompletedTaskNodeId(
  taskId: string,
  mediaType: Extract<CanvasNode['mediaType'], 'image' | 'video' | '3d_model'>,
): string {
  return uuidv5(`${taskId}::${mediaType}`, TASK_COMPLETION_NODE_NAMESPACE);
}

export function buildTaskCandidateNodeId(taskId: string, outputIndex: number): string {
  return uuidv5(`${taskId}::candidate::${outputIndex}`, TASK_CANDIDATE_NODE_NAMESPACE);
}

/**
 * The backend reserves one durable canvas node id before task submission and
 * stores it on the task. Recovery must reuse that id instead of inventing a
 * second identity for the same output.
 */
export function getAuthoritativeTaskCanvasNodeId(
  inputParams?: Record<string, unknown> | null,
): string | undefined {
  if (!inputParams) return undefined;

  const rawValue = inputParams.canvas_node_id ?? inputParams.canvasNodeId;
  if (typeof rawValue !== 'string') return undefined;

  const normalized = rawValue.trim();
  return UUID_PATTERN.test(normalized) ? normalized : undefined;
}

export function buildTemplateRunOutputNodeId(runId: string, outputIndex: number): string {
  return uuidv5(`${runId}::${outputIndex}`, TEMPLATE_RUN_OUTPUT_NODE_NAMESPACE);
}

export function buildUgcStoryboardBoardNodeId(projectId: string, storyboardId: string): string {
  const identity = `${projectId.trim()}::${storyboardId.trim()}`;
  return uuidv5(`ugc-storyboard-board::${identity}`, UGC_STORYBOARD_BOARD_NODE_NAMESPACE);
}

export function buildUgcSequenceNodeId(taskIds: string[]): string {
  const identity = taskIds.slice(0, 3).map((taskId) => taskId.trim()).join('::');
  return uuidv5(`ugc-sequence::${identity}`, UGC_SEQUENCE_NODE_NAMESPACE);
}

export function buildUgcFinalVideoNodeId(sequenceNodeId: string): string {
  return uuidv5(`ugc-final-video::${sequenceNodeId.trim()}`, UGC_FINAL_VIDEO_NODE_NAMESPACE);
}

/**
 * Compatibility identity for servers that predate the canonical
 * `canvas_node_id` response. Generic breakdown storyboards belong to their
 * project/source pair. UGC flows use the same Candidate Run scope as the
 * backend, so reselection replaces the current run's Storyboard while a new
 * candidate run remains a separate branch.
 */
export function buildStoryboardAnalysisNodeId(
  projectId: string,
  sourceNodeId: string,
  flowId?: string | null,
): string {
  const project = projectId.trim();
  const normalizedFlowId = flowId?.trim();
  const sourceIdentity = normalizedFlowId
    ? `scope::ugc-reference::${normalizedFlowId}`
    : `node::${sourceNodeId.trim()}`;
  const storyboardId = uuidv5(
    `video-storyboard::${project}::${sourceIdentity}`,
    VIDEO_STORYBOARD_NAMESPACE,
  );
  return uuidv5(
    `video-storyboard-node::${project}::${storyboardId}`,
    VIDEO_STORYBOARD_CANVAS_NODE_NAMESPACE,
  );
}

export function isCurrentStoryboardSnapshotRevision(
  node: Pick<CanvasNode, 'mediaType' | 'promptMetadata'>,
  expectedTaskId: string,
  expectedRevision?: number | null,
): boolean {
  if (
    node.mediaType !== 'prompt'
    || node.promptMetadata?.kind !== 'storyboard_analysis_summary'
    || node.promptMetadata.task_id !== expectedTaskId
  ) {
    return false;
  }
  if (expectedRevision == null) return true;
  return Number(node.promptMetadata.storyboard_revision) === expectedRevision;
}

function buildTemplatePromptSeedNodeId(templateNodeId: string): string {
  return uuidv5(`template-prompt-seed::${templateNodeId}`, TEMPLATE_PROMPT_SEED_NODE_NAMESPACE);
}

export function getRelationTypeForCapability(
  capabilityId?: string,
): 'ai-generated' | 'style-transfer' | 'transition' | 'composite' {
  switch (capabilityId) {
    case 'style_transfer':
      return 'style-transfer';
    case 'multi_image_to_video':
    case 'omni_video':
      return 'transition';
    case 'face_swap':
    case 'outfit_swap':
      return 'composite';
    default:
      return 'ai-generated';
  }
}

function normalizeMaterializationUrl(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function resolveTemplateMaterializationIdentity(node: MaterializationKeyNode): string | null {
  return normalizeMaterializationUrl(node.videoUrl)
    ?? normalizeMaterializationUrl(node.modelUrl)
    ?? normalizeMaterializationUrl(node.originalImageUrl)
    ?? normalizeMaterializationUrl(node.thumbnailUrl)
    ?? normalizeMaterializationUrl(node.thumbnail);
}

function resolveTemplatePromptSeedTemplateNodeId(node: MaterializationKeyNode): string | null {
  if (node.mediaType !== 'prompt' || !node.promptMetadata || typeof node.promptMetadata !== 'object') {
    return null;
  }

  const promptMetadata = node.promptMetadata as Record<string, unknown>;
  if (promptMetadata.template_prompt_seed === true
    && typeof promptMetadata.template_prompt_template_node_id === 'string'
    && promptMetadata.template_prompt_template_node_id.trim().length > 0
  ) {
    return promptMetadata.template_prompt_template_node_id.trim();
  }

  return promptMetadata.workflow_prompt_seed === true
    && typeof promptMetadata.workflow_prompt_template_node_id === 'string'
    && promptMetadata.workflow_prompt_template_node_id.trim().length > 0
    ? promptMetadata.workflow_prompt_template_node_id.trim()
    : null;
}

function resolveTemplatePromptSeedSlotKey(node: MaterializationKeyNode): string | null {
  if (node.mediaType !== 'prompt' || !node.promptMetadata || typeof node.promptMetadata !== 'object') {
    return null;
  }

  const promptMetadata = node.promptMetadata as Record<string, unknown>;
  if (typeof promptMetadata.workflow_prompt_slot_key === 'string' && promptMetadata.workflow_prompt_slot_key.trim().length > 0) {
    return promptMetadata.workflow_prompt_slot_key.trim();
  }

  return typeof promptMetadata.template_prompt_slot_key === 'string' && promptMetadata.template_prompt_slot_key.trim().length > 0
    ? promptMetadata.template_prompt_slot_key.trim()
    : '__template_default_prompt__';
}

export function buildCanvasNodeMaterializationKey(node: MaterializationKeyNode): string | null {
  if (node.mediaType === 'sequence') {
    const sequenceData = node.sequenceData && typeof node.sequenceData === 'object'
      ? node.sequenceData as unknown as Record<string, unknown>
      : {};
    const sequenceStoryboard = sequenceData.brandStoryboard && typeof sequenceData.brandStoryboard === 'object'
      ? sequenceData.brandStoryboard as unknown as Record<string, unknown>
      : {};
    const storyboardData = node.storyboardData && typeof node.storyboardData === 'object'
      ? node.storyboardData as Record<string, unknown>
      : {};
    const storyboardSession = storyboardData.storyboard_session
      && typeof storyboardData.storyboard_session === 'object'
      ? storyboardData.storyboard_session as Record<string, unknown>
      : {};
    const ugcStoryboardId = (
      typeof storyboardSession.storyboard_id === 'string'
        ? storyboardSession.storyboard_id
        : ''
    ).trim();
    if (sequenceData.kind === 'ugc_storyboard' && ugcStoryboardId) {
      return ['ugc_storyboard_board', ugcStoryboardId].join('::');
    }
    const storedStoryboard = storyboardData.brand_storyboard && typeof storyboardData.brand_storyboard === 'object'
      ? storyboardData.brand_storyboard as Record<string, unknown>
      : {};
    const storyboardId = (
      typeof sequenceStoryboard.storyboard_id === 'string'
        ? sequenceStoryboard.storyboard_id
        : typeof storedStoryboard.storyboard_id === 'string'
          ? storedStoryboard.storyboard_id
          : ''
    ).trim();
    if (storyboardId) {
      return ['brand_storyboard_board', storyboardId].join('::');
    }
  }

  if (node.mediaType === 'prompt' && node.promptMetadata && typeof node.promptMetadata === 'object') {
    const promptMetadata = node.promptMetadata as Record<string, unknown>;
    const storyboardBoardNodeId = typeof promptMetadata.canvas_node_id === 'string'
      ? promptMetadata.canvas_node_id
      : typeof promptMetadata.storyboard_board_node_id === 'string'
        ? promptMetadata.storyboard_board_node_id
        : null;
    if (storyboardBoardNodeId) {
      return ['storyboard_prompt', storyboardBoardNodeId].join('::');
    }

    if (
      promptMetadata.kind === 'storyboard_analysis_summary'
      && typeof promptMetadata.task_id === 'string'
      && promptMetadata.task_id.trim().length > 0
    ) {
      return ['storyboard_prompt_task', promptMetadata.task_id.trim()].join('::');
    }

    const templatePromptSeedTemplateNodeId = resolveTemplatePromptSeedTemplateNodeId(node);
    if (templatePromptSeedTemplateNodeId) {
      return [
        'template_prompt_seed',
        templatePromptSeedTemplateNodeId,
        resolveTemplatePromptSeedSlotKey(node) || '__template_default_prompt__',
      ].join('::');
    }
  }

  const capability = node.generatingCapability || node.generationCapability;
  const taskId = node.generatingTaskId || node.generatedByTaskId;
  if (!taskId) {
    return null;
  }

  if (
    taskId.startsWith('preparing-')
    && typeof node.generationGroupId === 'string'
    && node.generationGroupId.startsWith('template-')
  ) {
    return ['template_run_output_placeholder', node.generationGroupId, node.id].join('::');
  }

  if (capability === 'element_decomposition' && typeof node._elementIndex === 'number') {
    return ['element_decomposition', taskId, String(node._elementIndex), node.decompositionParentId || ''].join('::');
  }

  if (typeof node.outputIndex === 'number') {
    return ['task_candidate_node', taskId, String(node.outputIndex)].join('::');
  }

  if (capability === 'template') {
    const templateIdentity = resolveTemplateMaterializationIdentity(node);
    return ['template_task_node', taskId, node.mediaType, templateIdentity || 'pending'].join('::');
  }

  return ['task_node', taskId, node.mediaType].join('::');
}

function buildEdgeCountByNodeId(edges: ReadonlyArray<Pick<CanvasEdge, 'source' | 'target'>>): Map<string, number> {
  const counts = new Map<string, number>();

  edges.forEach((edge) => {
    counts.set(edge.source, (counts.get(edge.source) ?? 0) + 1);
    counts.set(edge.target, (counts.get(edge.target) ?? 0) + 1);
  });

  return counts;
}

function scoreMaterializedCanvasNode(node: CanvasNode, edgeCountByNodeId: ReadonlyMap<string, number>): number {
  let score = 0;
  const templatePromptSeedTemplateNodeId = resolveTemplatePromptSeedTemplateNodeId(node);
  const templatePromptSeedSlotKey = resolveTemplatePromptSeedSlotKey(node);

  score += (edgeCountByNodeId.get(node.id) ?? 0) * 100;
  if (node.generatedByTaskId && !node.generatingTaskId) score += 50;
  if (node.assetId) score += 40;
  if (node.workflowAreaGroupId) score += 30;
  if (node.lineageSourceNodeIds?.length) score += 25;
  if (node.lineageRelationType) score += 15;
  if (node.generationCapability || node.generatingCapability) score += 10;
  if (node.generationGroupId) score += 8;
  if (node.sourceLabel) score += 8;
  if (node.templateInstance) score += 6;
  if (node.videoUrl || node.modelUrl || node.originalImageUrl || node.thumbnailUrl || node.thumbnail) score += 5;
  if (node.lastActivityAt) score += 2;
  if (node.enteredCanvasAt) score += 1;
  if (
    templatePromptSeedTemplateNodeId
    && templatePromptSeedSlotKey === '__template_default_prompt__'
    && node.id === buildTemplatePromptSeedNodeId(templatePromptSeedTemplateNodeId)
  ) score += 20;

  return score;
}

export function collapseMaterializedCanvasNodes(
  nodes: CanvasNode[],
  edges: ReadonlyArray<Pick<CanvasEdge, 'source' | 'target'>> = [],
): { nodes: CanvasNode[]; duplicateNodeIds: string[] } {
  const edgeCountByNodeId = buildEdgeCountByNodeId(edges);
  const groupedNodes = new Map<string, CanvasNode[]>();

  nodes.forEach((node) => {
    const materializationKey = buildCanvasNodeMaterializationKey(node) || `id::${node.id}`;
    const currentGroup = groupedNodes.get(materializationKey);
    if (currentGroup) {
      currentGroup.push(node);
      return;
    }
    groupedNodes.set(materializationKey, [node]);
  });

  const keepNodeIds = new Set<string>();
  groupedNodes.forEach((groupNodes) => {
    if (groupNodes.length === 1) {
      keepNodeIds.add(groupNodes[0].id);
      return;
    }

    const keepNode = [...groupNodes].sort((left, right) => (
      scoreMaterializedCanvasNode(right, edgeCountByNodeId) - scoreMaterializedCanvasNode(left, edgeCountByNodeId)
    ))[0];
    keepNodeIds.add(keepNode.id);
  });

  const dedupedNodes: CanvasNode[] = [];
  const duplicateNodeIds: string[] = [];
  const seenKeptNodeIds = new Set<string>();

  nodes.forEach((node) => {
    if (keepNodeIds.has(node.id) && !seenKeptNodeIds.has(node.id)) {
      seenKeptNodeIds.add(node.id);
      dedupedNodes.push(node);
      return;
    }

    duplicateNodeIds.push(node.id);
  });

  return {
    nodes: dedupedNodes,
    duplicateNodeIds,
  };
}

export function buildSimpleLineagePatch(
  sourceNodeIds: Array<string | null | undefined>,
  relationType?: NodeRelationType | null,
): Partial<Pick<CanvasNode, 'lineageSourceNodeIds' | 'lineageRelationType'>> {
  const normalizedSourceNodeIds = Array.from(new Set(
    sourceNodeIds
      .filter((value): value is string => typeof value === 'string')
      .map((value) => value.trim())
      .filter((value) => value.length > 0),
  ));
  if (normalizedSourceNodeIds.length === 0 || !relationType) {
    return {};
  }

  return {
    lineageSourceNodeIds: normalizedSourceNodeIds,
    lineageRelationType: relationType,
  };
}
